/**
 * EditUserModal Component
 *
 * Form modal for editing user's first name, last name, and roles.
 * The actual update is delegated via the `onSubmit` callback; `children` adds read-only content
 * after the role field. `nameReadOnly` locks the name fields, `notice` explains why.
 *
 * Uses `useTranslation('modules.modals.editUser')` for labels.
 *
 * @example
 * ```tsx
 * <EditUserModal
 *   open={!!editingUser}
 *   user={{ id: '1', email: 'user@example.com', firstName: 'Jan', lastName: 'Novák', roles: ['USER'] }}
 *   roleOptions={[{ value: 'ADMIN', label: 'Admin' }]}
 *   onSubmit={async (data) => await updateUser(data)}
 *   onClose={() => setEditingUser(null)}
 * />
 * ```
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type TestIdProps } from '../../utils';
import { FormModal } from '../../components/FormModal';
import { Input } from '../../components/inputs';
import { InputWrapper } from '../../components/InputWrapper';
import { Select } from '../../components/selects/Select';
import { Toggle } from '../../components/Toggle';
import { Modal } from '../../components/Modal';
import { useTranslation } from '../../providers/UIProvider';

export interface EditUserFormData {
  firstName: string;
  lastName: string;
  roles: string[];
  /** Only present when the modal renders the active toggle. */
  active?: boolean;
}

export interface EditUserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

export interface EditUserModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** User data to edit */
  user: EditUserData;
  /** Available role options for the multi-select; a description shows under its option */
  roleOptions: Array<{ value: string; label: string; description?: string }>;
  /** Optional function to extract assignable roles from user's current roles */
  getAssignableRoles?: (roles: string[]) => string[];
  /** Render a toggle for the user's active state */
  showActiveToggle?: boolean;
  /** Cautionary note shown above the fields — e.g. this edit removes the last admin */
  warning?: React.ReactNode;
  /** Informational note shown above the fields — e.g. why the name is locked */
  notice?: React.ReactNode;
  /** Show the name fields disabled and skip their validation */
  nameReadOnly?: boolean;
  /** Initial value of the active toggle */
  active?: boolean;
  /** Called with form data — should return on success or throw on error */
  onSubmit: (data: EditUserFormData) => Promise<void>;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Whether the submission is in progress */
  loading?: boolean;
  /** External error message */
  error?: string | null;
  /** Extra content rendered after the role field, e.g. a read-only section */
  children?: React.ReactNode;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  roleOptions,
  getAssignableRoles,
  showActiveToggle = false,
  active = true,
  warning,
  notice,
  nameReadOnly = false,
  onSubmit,
  onClose,
  loading = false,
  error: externalError,
  children
}) => {
  const t = useTranslation('modules.modals.editUser');

  const buildFormData = (): EditUserFormData => ({
    firstName: user.firstName,
    lastName: user.lastName,
    roles: getAssignableRoles ? getAssignableRoles(user.roles) : user.roles,
    ...(showActiveToggle ? { active } : {})
  });

  const [formData, setFormData] = useState<EditUserFormData>(buildFormData);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      roles: getAssignableRoles ? getAssignableRoles(user.roles) : user.roles,
      ...(showActiveToggle ? { active } : {})
    });
  }, [user, getAssignableRoles, showActiveToggle, active]);

  const handleSubmit = async () => {
    setLocalError(null);

    if (!nameReadOnly && !formData.firstName.trim()) {
      setLocalError(t('errors.firstNameRequired'));
      return;
    }
    if (!nameReadOnly && !formData.lastName.trim()) {
      setLocalError(t('errors.lastNameRequired'));
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('errors.updateError');
      setLocalError(errorMsg);
    }
  };

  const handleRolesChange = (value: string | number | (string | number)[]) => {
    const roles = Array.isArray(value) ? value.map(v => String(v)) : [String(value)];
    setFormData({ ...formData, roles });
  };

  const displayError = localError || externalError || null;

  return (
    <FormModal
      open={open}
      title={t('title')}
      description={t('description')}
      submitLabel={loading ? t('status.updating') : t('actions.save')}
      cancelLabel={t('actions.cancel')}
      loading={loading}
      error={displayError}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      {warning && <Modal.Warning>{warning}</Modal.Warning>}
      {notice && <Modal.Info>{notice}</Modal.Info>}

      <InputWrapper label={t('fields.email')} htmlFor='email' labelSize='sm'>
        <Input id='email' type='email' value={user.email} disabled />
      </InputWrapper>

      <InputWrapper
        label={t('fields.firstName')}
        htmlFor='firstName'
        labelSize='sm'
        required={!nameReadOnly}
      >
        <Input
          id='firstName'
          type='text'
          placeholder={t('placeholders.firstName')}
          value={formData.firstName}
          onChange={e => setFormData({ ...formData, firstName: e.target.value })}
          disabled={loading || nameReadOnly}
          required={!nameReadOnly}
        />
      </InputWrapper>

      <InputWrapper
        label={t('fields.lastName')}
        htmlFor='lastName'
        labelSize='sm'
        required={!nameReadOnly}
      >
        <Input
          id='lastName'
          type='text'
          placeholder={t('placeholders.lastName')}
          value={formData.lastName}
          onChange={e => setFormData({ ...formData, lastName: e.target.value })}
          disabled={loading || nameReadOnly}
          required={!nameReadOnly}
        />
      </InputWrapper>

      <InputWrapper label={t('fields.role')} htmlFor='role' labelSize='sm' className='min-h-96'>
        <Select
          id='role'
          isMulti
          value={formData.roles}
          onChange={handleRolesChange}
          options={roleOptions}
          disabled={loading}
        />
      </InputWrapper>

      {children}

      {showActiveToggle && (
        <InputWrapper label={t('fields.active')} htmlFor='active' labelSize='sm'>
          <Toggle
            id='active'
            checked={formData.active ?? false}
            onChange={value => setFormData({ ...formData, active: value })}
            disabled={loading}
          />
        </InputWrapper>
      )}
    </FormModal>
  );
};

EditUserModal.displayName = 'EditUserModal';
